// v4 lichen rock — REDESIGNED. An ANGULAR weathered slab — a leaning block
// with a sharp shoulder shard and a foot chip — its tilted top face painted
// with FLAT pale lichen discs (laid exactly along the face plane) and
// squashed dots spilling down the sides, so the lichen reads as a crust, not
// pebbles. Variants turn the slab, swap the grey and re-scatter the crust.
//
// Ported 1:1 from age-of-mcp's v4/lichen-rock.ts, using the library's shared
// `groundcover` helpers (h01/lerp) — identical to the v4 source's.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const { h01, lerp } = groundcover;

const GREYS: [string, string][] = [
  ["#7c7f86", "#6b6e75"],
  ["#84878e", "#71747b"],
  ["#75787f", "#63666d"],
];
const LICHEN = ["#b8c4a4", "#cdd6b8", "#9fb28c"];

function partsFor(v: number): Part[] {
  const seed = v * 23 + 11;
  const yaw = v * 2.1 + 0.7;
  const [grey, dark] = GREYS[v % GREYS.length]!;
  const rx = 0.14, rz = -0.18;                       // the slab's lean
  // map a point in the slab's local space to world (so the lichen discs
  // can sit exactly on the tilted faces)
  const slabE = new THREE.Euler(rx, yaw, rz, "XYZ");
  const onSlab = (lx: number, ly: number, lz: number): [number, number, number] => {
    const p = new THREE.Vector3(lx, ly, lz).applyEuler(slabE);
    return [p.x, p.y + 0.155, p.z];
  };
  const parts: Part[] = [
    // the main leaning slab
    { geometry: new THREE.BoxGeometry(0.34, 0.34, 0.27), color: grey,
      position: [0, 0.155, 0], rotation: [rx, yaw, rz] },
    // sharp shoulder shard — tucked into the slab so it reads as a chip
    // grown from the rock, not a flat flap jutting out and dipping below it
    { geometry: new THREE.TetrahedronGeometry(0.1, 0), color: dark,
      position: [0.11, 0.16, 0.06], rotation: [0.4, yaw + 1.2, 0.3],
      scale: [1.0, 1.1, 1.0] },
    // foot chip
    { geometry: new THREE.IcosahedronGeometry(0.065, 0), color: grey,
      position: [-0.2, 0.045, -0.09], rotation: [0, yaw, 0.2],
      scale: [1.2, 0.7, 1.0] },
  ];
  // lichen crust — flat discs lying on the slab's tilted top face
  for (let i = 0; i < 3; i++) {
    const ox = lerp(-0.08, 0.08, h01(seed, i));
    const oz = lerp(-0.06, 0.06, h01(seed, i + 4));
    parts.push({
      geometry: new THREE.CylinderGeometry(
        lerp(0.04, 0.06, h01(seed, i + 8)),
        lerp(0.05, 0.07, h01(seed, i + 8)), 0.012, 6),
      color: LICHEN[i % LICHEN.length]!,
      position: onSlab(ox, 0.166, oz),
      rotation: [rx, yaw, rz],
    });
  }
  // squashed lichen dots spilling down the sides
  for (let i = 0; i < 2; i++) {
    parts.push({
      geometry: new THREE.IcosahedronGeometry(0.026, 0),
      color: LICHEN[(i + 1) % LICHEN.length]!,
      position: onSlab(lerp(-0.1, 0.1, h01(seed, i + 12)), 0.1, i ? 0.132 : -0.132),
      rotation: [rx + (i ? 0.9 : -0.9), yaw, rz],
      scale: [1, 0.35, 1],
    });
  }
  return parts;
}

export const lichenRockV4 = defineModel({
  id: "lichen-rock-v4",
  batchId: "polished",
  name: "Lichen rock (v4)",
  tags: ["scenery","lichen","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly lichen rock.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.4,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
    { id: "variant-2", label: "Variant 3", parts: () => partsFor(2) },
  ],
  animations: [],
});
